import axios from "axios";
import { getToken } from "./utilsApi";

/**
 * Student demographics import (phase 4). DiscloSure admins only (enforced by the server).
 * Values are uploaded but never read back — the server only returns counts.
 */
const url = () => `${process.env.REACT_APP_apiBaseUrl}demographics`;
export const ROWS_PER_REQUEST = 500;

const fail = (error) => ({
  ok: false,
  message: error?.response?.data?.message || "Something went wrong. Please try again.",
});

export const demographicsSummary = async (district) => {
  const token = await getToken();
  try {
    const res = await axios({ method: "GET", url: url(), headers: { "x-api-key": token }, params: { district } });
    return { ok: true, ...res.data };
  } catch (error) {
    return fail(error);
  }
};

export const uploadDemographics = async (district, rows) => {
  const token = await getToken();
  try {
    const res = await axios({
      method: "POST",
      url: url(),
      headers: { "x-api-key": token, "Content-Type": "application/json" },
      data: { district, rows },
      timeout: 30000,
    });
    return { ok: true, ...res.data };
  } catch (error) {
    return fail(error);
  }
};

/** Minimal CSV parser (quotes, commas and newlines inside quotes). Returns an array of rows. */
export const parseCsv = (text) => {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  const s = (text || "").replace(/^﻿/, "");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"' && s[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((v) => v.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((v) => v.trim() !== "")) rows.push(row);
  return rows;
};

const HEADER_ALIASES = {
  email: ["email", "student email", "email address", "studentemail"],
  studentNumber: ["student number", "studentnumber", "student id", "studentid", "sis id", "local id", "id"],
  grade: ["grade", "grade level", "gradelevel"],
  gender: ["gender", "sex"],
  race: ["race"],
  ethnicity: ["ethnicity", "hispanic", "hispanic/latino", "hispanic or latino"],
};

/** CSV text -> { rows: [{email, studentNumber, grade, gender, race, ethnicity}], missing: [...] } */
export const csvToRows = (text) => {
  const table = parseCsv(text);
  if (!table.length) return { rows: [], missing: ["a header row"] };
  const header = table[0].map((h) => h.trim().toLowerCase());
  const col = {};
  for (const [field, names] of Object.entries(HEADER_ALIASES)) {
    const idx = header.findIndex((h) => names.includes(h));
    if (idx >= 0) col[field] = idx;
  }
  const missing = [];
  if (col.email === undefined && col.studentNumber === undefined) missing.push("an Email or Student Number column");
  if (!["grade", "gender", "race", "ethnicity"].some((f) => col[f] !== undefined)) missing.push("at least one of Grade, Gender, Race, Ethnicity");
  const rows = table.slice(1).map((r) => {
    const out = {};
    for (const [field, idx] of Object.entries(col)) out[field] = (r[idx] || "").trim();
    return out;
  });
  return { rows, missing };
};

export const TEMPLATE_CSV = "Email,Student Number,Grade,Gender,Race,Ethnicity\nstudent@district.org,100234,7,Female,Asian,Not Hispanic or Latino\n";
